import { defineComponent as _defineComponent } from 'vue'
import { computed } from '@nuxtjs/composition-api';
import {
  forEachSections,
  sectionsWithDataProps,
} from '@webplatform/asbis-ui/dist/sections';
import { kebabToPascal, pascalToKebab, prepareDynamicBlock } from '~/helpers';
import ProductDetails from '~/components/sections/productSection/ProductDetails.vue';
import ProductSeriesHeader from '~/components/sections/productSection/ProductSeriesHeader.vue';
import ProductSeries from '~/components/sections/productSection/ProductSeries.vue';
import ProductWarrantyBanner from '~/components/sections/productSection/ProductWarrantyBanner.vue';
import ProductContentBanner from '~/components/sections/productSection/ProductContentBanner.vue';
import ProductFeatures from '~/components/sections/productSection/ProductFeatures.vue';
import ProductFeaturesBanner from '~/components/sections/productSection/ProductFeaturesBanner.vue';
import ProductCertificates from '~/components/sections/productSection/ProductCertificates.vue';
import LatestNews from '~/components/sections/newsSection/LatestNews.vue';
import BannersSlider from '~/components/sections/sliderSection/BannersSlider.vue';
import SolutionsCarousel from '~/components/sections/sliderSection/SolutionsCarousel.vue';
import CategoriesSection from '~/components/sections/categorySection/CategoriesSection.vue';
import CategoryBanner from '~/components/sections/categorySection/CategoryBanner.vue';
import CategoryBannerV2 from '~/components/sections/categorySection/CategoryBannerV2.vue';
import CategoryInfoBlocks from '~/components/sections/categorySection/CategoryInfoBlocks.vue';
import CategoryFeatures from '~/components/sections/categorySection/CategoryFeatures.vue';
import CategoryAccessories from '~/components/sections/categorySection/CategoryAccessories.vue';
import CategoryApplications from '~/components/sections/categorySection/CategoryApplications.vue';
import CategoryFaq from '~/components/sections/categorySection/CategoryFaq.vue';
import CategoryProductSeries from '~/components/sections/categorySection/CategoryProductSeries.vue';
import CategoryRecommended from '~/components/sections/categorySection/CategoryRecommended.vue';
import CategoryRecommendedProducts from '~/components/sections/categorySection/CategoryRecommendedProducts.vue';
import SoftwareCategoryFirstScreen from '~/components/sections/categorySection/SoftwareCategoryFirstScreen.vue';
import SoftwareCategoryAwards from '~/components/sections/categorySection/SoftwareCategoryAwards.vue';
import SoftwareCategoryInfoblocks from '~/components/sections/categorySection/SoftwareCategoryInfoblocks.vue';
import SoftwareCategoryReviewsSlider from '~/components/sections/categorySection/SoftwareCategoryReviewsSlider.vue';
import SoftwareCategoryTryDemo from '~/components/sections/categorySection/SoftwareCategoryTryDemo.vue';
import SoftwareCategoryTryDemoFormModal from '~/components/sections/categorySection/SoftwareCategoryTryDemoFormModal.vue';
import SoftwareCategoryChoosePlan from '~/components/sections/categorySection/SoftwareCategoryChoosePlan.vue';
import SoftwareCategoryUserHelp from '~/components/sections/categorySection/SoftwareCategoryUserHelp.vue';
import CatalogCategories from '~/components/sections/categorySection/CatalogCategories.vue';
import ConsultationForm from '~/components/sections/formSection/ConsultationForm.vue';
import ServiceCentersSection from '~/components/sections/serviceCentersSection/ServiceCentersSection.vue';
import CtaSectionWithBackground from '~/components/sections/ctaSection/CtaSectionWithBackground.vue';
import CtaSectionBanner from '~/components/sections/ctaSection/CtaSectionBanner.vue';
import ProductBenefits from '~/components/sections/productSection/ProductBenefits.vue';
import ProductFootnote from '~/components/sections/productSection/ProductFootnote.vue';
import ProductVideo from '~/components/sections/productSection/ProductVideo.vue';
import PartnersSection from '~/components/sections/categorySection/PartnersSection.vue';
import ServiceForm from '~/components/sections/formSection/ServiceForm.vue';
import CatalogHead from '~/domains/catalog/components/CatalogHead.vue';
import HtmlContentBlock from '~/components/HtmlContentBlock.vue';
import BookMeetingPopup from '~/domains/book-meeting/components/BookMeetingPopup.vue';

interface Props {
  blocks?: any[];
  product?: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PageBlocks',
  props: {
    blocks: { default: () => [] },
    product: { default: (value: any) => value || null }
  },
  setup(__props: any) {

const props = __props as { blocks: any[], product: any };

const NO_COMPONENT_TYPE = [
  'product-series-additional-information',
  'software-documents',
  'marketing_type',
];

const EXCLUDED_SECTIONS_FROM_ASBIS_UI = ['CtaSectionBanner'];

const localSections = {
  CatalogCategories,
  CatalogHead,
  ProductDetails,
  ProductSeriesHeader,
  ProductSeries,
  ProductFeaturesBanner,
  ProductCertificates,
  ProductWarrantyBanner,
  ProductContentBanner,
  ProductFeatures,
  BannersSlider,
  CategoriesSection,
  SolutionsCarousel,
  LatestNews,
  CategoryBanner,
  CategoryBannerV2,
  CategoryInfoBlocks,
  CategoryFeatures,
  CategoryProductSeries,
  CategoryApplications,
  CategoryAccessories,
  CategoryRecommended,
  CategoryFaq,
  ConsultationForm,
  SoftwareCategoryFirstScreen,
  SoftwareCategoryAwards,
  SoftwareCategoryInfoblocks,
  SoftwareCategoryReviewsSlider,
  SoftwareCategoryTryDemo,
  SoftwareCategoryTryDemoFormModal,
  SoftwareCategoryChoosePlan,
  SoftwareCategoryUserHelp,
  BookMeetingPopup,
  HtmlContentBlock,
  CategoryRecommendedProducts,
  ServiceCentersSection,
  CtaSectionWithBackground,
  CtaSectionBanner,
  ProductBenefits,
  ProductFootnote,
  ProductVideo,
  PartnersSection,
  ServiceForm,
};

const asbisUiSections: Record<string, any> = {};
forEachSections((sectionName, sectionComponent) => {
  if (EXCLUDED_SECTIONS_FROM_ASBIS_UI.includes(sectionName)) return;
  asbisUiSections[sectionName] = sectionComponent;
});

const components = {
  ...localSections,
  ...asbisUiSections,
};



const pageComponents = computed(() => {
  if (!props.blocks.length) return [];
  const preparedComponents = props.blocks
    .map((block) =>
      prepareDynamicBlock(block, {
        excludedBlocks: NO_COMPONENT_TYPE,
        noDataPropsSections: EXCLUDED_SECTIONS_FROM_ASBIS_UI.map(
          (sectionName) => pascalToKebab(sectionName),
        ),
        sectionsWithDataProps,
      }),
    )
    .filter(
      (item) =>
        'type' in item &&
        components[kebabToPascal(item.type) as keyof typeof components],
    );
  return preparedComponents;
});

const isAsbisComponent = (componentType: string) => {
  return Object.keys(asbisUiSections).includes(componentType);
};

const getComponentToRender = (componentType: string) =>
  components[kebabToPascal(componentType) as keyof typeof components];

const getComponentType = (componentType: string) =>
  kebabToPascal(componentType);

const getComponentBindingData = (component: { type: string; data: any }) =>
  isAsbisComponent(kebabToPascal(component.type))
    ? component.data
    : { data: component.data, product: props.product };

return { __sfc: true,NO_COMPONENT_TYPE, EXCLUDED_SECTIONS_FROM_ASBIS_UI, localSections, asbisUiSections, components, props, pageComponents, isAsbisComponent, getComponentToRender, getComponentType, getComponentBindingData }
}

})